import * as React from 'react';
import { generateUuid } from '@utils/uuid';
import { ProgressMessage } from '../../components/common/FileUpload/UploadProgressModal';
import { ApiErrors } from '../../lib/api/types';
import { Packet, PacketQuestion } from '../../types/packet.schema';
import { PacketAnswer, SellersPacket } from '../../types/sellersPacket.schema';
import { useAsync } from '../useAsync';
import { useForm } from '../useForm';
import { getFormSchema, getInitialFormValues, getReuseFlagValues } from './formUtils';
import { getFormFieldId, packetAnswersToFormAnswers } from './preprocessing';
import { submitPacket } from './submission';
import { track } from '../../lib/analytics';
import { FileInputValue } from '../UseFileInput';
import { flushSync } from 'react-dom';

export type FieldFiles = Array<FileInputValue & { questionId: string }>;

// Internal representation of a packet answer. Drives the form fields.
export type Answer = Omit<PacketAnswer, 'value'> & {
  value: string | FileInputValue[] | Date | boolean[];
  id: string;
};

export type PacketFormFieldValue = Date | string | FileInputValue[] | boolean[] | boolean;

export type PacketFormValues = Record<string, PacketFormFieldValue> & {
  autosell: boolean;
  donation: string;
};

export type UseSellersPacketFormResult = ReturnType<typeof useSellersPacketForm>;

const useSellersPacketForm = ({
  packet,
  sellersPacket,
  isFrictionless = false
}: {
  packet: Packet;
  sellersPacket?: SellersPacket;
  isFrictionless?: boolean;
}) => {
  // For frictionless we will not prefill completed answers, we will simply not show them those questions
  const initialSellersPacket = isFrictionless ? emptySellersPacket : sellersPacket;
  const [answers, setAnswers] = React.useState<Answer[]>(() =>
    packetAnswersToFormAnswers(packet.questions, initialSellersPacket.answers)
  );

  React.useEffect(() => {
    track('packetView', ['tartle'], { packetId: packet.id, isFrictionless });
  }, [packet.id, isFrictionless]);

  const [reuseFlags, setReuseFlags] = React.useState<Record<string, boolean>>(() =>
    getReuseFlagValues(packet.questions)
  );
  const {
    run,
    isLoading: isLoadingSubmitSellersPacket,
    isError: isSubmitSellersPacketError,
    error: submitSellersPacketError
  } = useAsync<SellersPacket, ApiErrors>();

  const {
    values,
    setValues,
    isDisabled: isFormDisabled,
    handleFieldChange,
    schema,
    errors,
    isFieldVisited,
    setIsFormDirty
  } = useForm({
    onSubmit: () => null,
    schema: React.useMemo(
      () => getFormSchema(packet.questions, answers),
      [answers, packet.questions]
    ),
    initialValues: () => getInitialFormValues(answers, initialSellersPacket),
    beforeUnload: true
  });

  const addAnswer = (question: PacketQuestion) => {
    const id = getFormFieldId(question.id, generateUuid());
    // This will refresh the schema
    setAnswers(prev => [
      ...prev,
      {
        id,
        questionId: question.id,
        value: ''
      }
    ]);
    setValues({ ...values, [id]: '' });
  };
  const removeAnswer = (id: string) => {
    setAnswers(prev => prev.filter(answer => answer.id !== id));
    setValues(
      Object.fromEntries(
        Object.entries(values).filter(([key]) => key !== id)
      ) as PacketFormValues
    );
  };

  const submitSellersPacket = async ({
    submitType,
    authToken,
    setUploadProgress = () => null,
    toggleUploadProgressModal = () => null,
    pubkey
  }: {
    submitType: 'save' | 'publish' | 'save-configuration';
    authToken?: string;
    setUploadProgress?: React.Dispatch<React.SetStateAction<ProgressMessage>>;
    toggleUploadProgressModal?: () => void;
    pubkey: string;
  }) =>
    run(
      submitPacket({
        submitType,
        packet,
        requiresEncryption,
        formValues: values,
        updateProgress: setUploadProgress,
        toggleUploadProgressModal,
        previousStatus: sellersPacket?.status || 'unpublished',
        schema,
        pubkey,
        authToken,
        isFrictionless,
        answersToAppend: sellersPacket?.answers || []
      })
        .then(res => {
          // Submitting sometimes results in a redirect, which in some cases can be prioritized
          // over any remaning state updates.
          flushSync(() => setIsFormDirty(false));
          return res;
        })
        .catch((error: unknown) => {
          if (error instanceof Error) {
            return Promise.reject([error.message]);
          }
          return Promise.reject(error);
        })
    );

  const requiresEncryption = React.useMemo(
    () => packet.questions.some(question => question.encrypted),
    [packet.questions]
  );

  return {
    answers,
    values,
    errors,
    requiresEncryption,
    handleFieldChange,
    isFormDisabled,
    isFieldVisited,
    addAnswer,
    removeAnswer,
    reuseFlags,
    setReuseFlags,
    submitSellersPacket,
    isLoadingSubmitSellersPacket,
    isSubmitSellersPacketError,
    submitSellersPacketError
  };
};

export { useSellersPacketForm };

export const emptySellersPacket: SellersPacket = {
  id: '',
  answers: [],
  autosellSetting: undefined,
  beneficiaryAccountId: undefined,
  status: 'unpublished'
};
